import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { UploadOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Radio, Row, Select, Upload } from "antd";
import { observer } from "mobx-react";
import { EIso3166OneAlpha2, isEuCountry } from "@devowl-wp/iso-codes";
import { AffiliatePanel } from "../affiliatePanel.js";
import { AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS } from "./changeFields.js";
import { usePriorityCountries } from "../../../../../../hooks/usePriorityCountries.js";
import { useStores } from "../../../../../../store/stores.js";
import { __, _i } from "../../../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../../../utils/matomoMask.js";
// Lifted shouldUpdate predicates avoid new function references on every render.
const updateOnCountry = (prev, next)=>prev?.affiliate?.invoiceAddress?.country !== next?.affiliate?.invoiceAddress?.country;
const updateOnCountryOrVatStatus = (prev, next)=>updateOnCountry(prev, next) || prev?.affiliate?.invoiceAddress?.isSmallBusinessWithoutVatDeclaration !== next?.affiliate?.invoiceAddress?.isSmallBusinessWithoutVatDeclaration;
const updateOnAddressChangeFields = (prev, next)=>JSON.stringify(AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS.map((k)=>next?.affiliate?.invoiceAddress?.[k])) !== JSON.stringify(AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS.map((k)=>prev?.affiliate?.invoiceAddress?.[k]));
// `tradeRegisterExcerpt` isn't part of the form-values type (server-only field name).
// Centralise the cast so the JSX stays cast-free.
const resetTradeRegisterExcerpt = (form)=>{
    form.setFieldsValue({
        affiliate: {
            invoiceAddress: {
                tradeRegisterExcerpt: undefined
            }
        }
    });
};
// Austria- and Germany-specific small-business VAT-exemption hint with a link to the relevant law.
const buildSmallBusinessExtra = (isAustria)=>_i(isAustria ? __('Are you a small business owner ("Kleinunternehmer") and exempt from VAT according to {{a}}Section 6 (1)(27) UStG{{/a}}?') : __('Are you a small business owner ("Kleinunternehmer") and exempt from VAT according to {{a}}Section 19 UStG{{/a}}?'), {
        a: /*#__PURE__*/ _jsx("a", {
            href: isAustria ? __("https://www.usp.gv.at/en/themen/steuern-finanzen/umsatzsteuer-ueberblick/weitere-informationen-zur-umsatzsteuer/weitere-steuertatbestaende-und-befreiungen/kleinunternehmen.html") : __("https://dejure.org/gesetze/UStG/19.html"),
            rel: "noreferrer",
            target: "_blank"
        })
    });
const buildTradeRegisterLabel = (isGermany, isAustria)=>isGermany ? __("Trade license (Gewerbeschein; PDF)") : isAustria ? __("Trade license (Gewerbeberechtigung; PDF)") : __("Trade register excerpt (PDF)");
const AffiliateInvoiceAddressFields = observer(({ isEdit, setTradeRegisterExcerptFile })=>{
    const countries = usePriorityCountries("iso-3166-1-alpha-2");
    const form = Form.useFormInstance();
    const { accountV2Store: { session } } = useStores();
    const handleResetExcerpt = ()=>{
        resetTradeRegisterExcerpt(form);
        setTradeRegisterExcerptFile(undefined);
    };
    return /*#__PURE__*/ _jsxs(AffiliatePanel, {
        heading: __("Invoice address"),
        subline: __("We use this address on the receipts for your commission payouts."),
        children: [
            /*#__PURE__*/ _jsx(Form.Item, {
                name: [
                    "affiliate",
                    "invoiceAddress",
                    "companyName"
                ],
                label: __("Company name"),
                extra: __('No registered company name (e.g. "Kleinunternehmer")? Enter your full name.'),
                rules: [
                    {
                        required: true,
                        message: __("Please enter a company name!")
                    }
                ],
                required: true,
                children: /*#__PURE__*/ _jsx(Input, {
                    ...MATOMO_MASK
                })
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                name: [
                    "affiliate",
                    "invoiceAddress",
                    "country"
                ],
                label: __("Country"),
                extra: __("If you have multiple branches, pick the one we pay commissions to - it sets your taxation."),
                required: true,
                rules: [
                    {
                        required: true,
                        message: __("Please choose your country!")
                    }
                ],
                children: /*#__PURE__*/ _jsxs(Select, {
                    autoComplete: "dontshow",
                    transitionName: null,
                    placeholder: __("Choose your country..."),
                    showSearch: true,
                    notFoundContent: __("No data"),
                    onChange: handleResetExcerpt,
                    optionFilterProp: "children",
                    children: [
                        /*#__PURE__*/ _jsx(Select.OptGroup, {
                            label: __("EU"),
                            children: countries.eu.map(({ key, label })=>/*#__PURE__*/ _jsx(Select.Option, {
                                    value: key,
                                    children: label
                                }, key))
                        }),
                        /*#__PURE__*/ _jsx(Select.OptGroup, {
                            label: __("Rest of the world"),
                            children: countries.restOfTheWorld.map(({ key, label })=>/*#__PURE__*/ _jsx(Select.Option, {
                                    value: key,
                                    children: label
                                }, key))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(Row, {
                gutter: 16,
                children: [
                    /*#__PURE__*/ _jsx(Col, {
                        xs: 24,
                        md: 12,
                        children: /*#__PURE__*/ _jsx(Form.Item, {
                            name: [
                                "affiliate",
                                "invoiceAddress",
                                "firstName"
                            ],
                            label: __("First name"),
                            required: true,
                            rules: [
                                {
                                    required: true,
                                    message: __("Please enter your first name!")
                                }
                            ],
                            children: /*#__PURE__*/ _jsx(Input, {
                                ...MATOMO_MASK
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        xs: 24,
                        md: 12,
                        children: /*#__PURE__*/ _jsx(Form.Item, {
                            name: [
                                "affiliate",
                                "invoiceAddress",
                                "lastName"
                            ],
                            label: __("Last name"),
                            required: true,
                            rules: [
                                {
                                    required: true,
                                    message: __("Please enter your last name!")
                                }
                            ],
                            children: /*#__PURE__*/ _jsx(Input, {
                                ...MATOMO_MASK
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                name: [
                    "affiliate",
                    "invoiceAddress",
                    "street"
                ],
                label: __("Street"),
                required: true,
                rules: [
                    {
                        required: true,
                        message: __("Please enter your street!")
                    }
                ],
                children: /*#__PURE__*/ _jsx(Input, {
                    ...MATOMO_MASK
                })
            }),
            /*#__PURE__*/ _jsxs(Row, {
                gutter: 16,
                children: [
                    /*#__PURE__*/ _jsx(Col, {
                        xs: 24,
                        md: 8,
                        children: /*#__PURE__*/ _jsx(Form.Item, {
                            name: [
                                "affiliate",
                                "invoiceAddress",
                                "zip"
                            ],
                            label: __("Postcode"),
                            required: true,
                            rules: [
                                {
                                    required: true,
                                    message: __("Please enter your postcode (ZIP code)!")
                                }
                            ],
                            children: /*#__PURE__*/ _jsx(Input, {
                                ...MATOMO_MASK
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        xs: 24,
                        md: 16,
                        children: /*#__PURE__*/ _jsx(Form.Item, {
                            name: [
                                "affiliate",
                                "invoiceAddress",
                                "city"
                            ],
                            label: __("City / Town"),
                            required: true,
                            rules: [
                                {
                                    required: true,
                                    message: __("Please enter your city/town!")
                                }
                            ],
                            children: /*#__PURE__*/ _jsx(Input, {
                                ...MATOMO_MASK
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: updateOnCountry,
                children: ({ getFieldValue })=>{
                    const country = getFieldValue([
                        "affiliate",
                        "invoiceAddress",
                        "country"
                    ]);
                    const isGermanyOrAustria = country === EIso3166OneAlpha2.Germany || country === EIso3166OneAlpha2.Austria;
                    if (!isGermanyOrAustria) {
                        return null;
                    }
                    return /*#__PURE__*/ _jsx(Form.Item, {
                        label: __("Small business"),
                        name: [
                            "affiliate",
                            "invoiceAddress",
                            "isSmallBusinessWithoutVatDeclaration"
                        ],
                        extra: buildSmallBusinessExtra(country === EIso3166OneAlpha2.Austria),
                        rules: [
                            {
                                required: true,
                                message: __("Please choose one option!")
                            }
                        ],
                        children: /*#__PURE__*/ _jsxs(Radio.Group, {
                            onChange: handleResetExcerpt,
                            children: [
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    value: true,
                                    children: __("Yes")
                                }),
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    value: false,
                                    children: __("No")
                                })
                            ]
                        })
                    });
                }
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: updateOnCountryOrVatStatus,
                children: ({ getFieldValue })=>{
                    const country = getFieldValue([
                        "affiliate",
                        "invoiceAddress",
                        "country"
                    ]);
                    if (country === undefined) {
                        return null;
                    }
                    const isEuCountryValue = isEuCountry(country);
                    const isSwitzerland = country === EIso3166OneAlpha2.Switzerland;
                    const isGermany = country === EIso3166OneAlpha2.Germany;
                    const isAustria = country === EIso3166OneAlpha2.Austria;
                    const isSmallBusinessWithoutVatDeclaration = isGermany || isAustria ? getFieldValue([
                        "affiliate",
                        "invoiceAddress",
                        "isSmallBusinessWithoutVatDeclaration"
                    ]) : false;
                    const isTradeRegisterExcerpt = !isEuCountryValue && !isSwitzerland || isGermany && isSmallBusinessWithoutVatDeclaration || isAustria && isSmallBusinessWithoutVatDeclaration;
                    const isCompanyIdentificationNumberRequired = !isEuCountryValue;
                    return /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            isEuCountryValue && isSmallBusinessWithoutVatDeclaration === false && /*#__PURE__*/ _jsx(Form.Item, {
                                label: __("VAT ID"),
                                name: [
                                    "affiliate",
                                    "invoiceAddress",
                                    "vatId"
                                ],
                                rules: [
                                    {
                                        required: true,
                                        message: __("Please enter your VAT ID!")
                                    }
                                ],
                                children: /*#__PURE__*/ _jsx(Input, {
                                    ...MATOMO_MASK
                                })
                            }),
                            isCompanyIdentificationNumberRequired && /*#__PURE__*/ _jsx(Form.Item, {
                                label: isSwitzerland ? "UID" : __("Company identification number"),
                                name: [
                                    "affiliate",
                                    "invoiceAddress",
                                    "companyIdentificationNumber"
                                ],
                                rules: [
                                    {
                                        required: true,
                                        message: isSwitzerland ? __("Please enter your UID!") : __("Please enter your company identification number!")
                                    }
                                ],
                                children: /*#__PURE__*/ _jsx(Input, {
                                    ...MATOMO_MASK
                                })
                            }),
                            (!isSwitzerland && !isEuCountryValue || isEuCountryValue && isSmallBusinessWithoutVatDeclaration) && /*#__PURE__*/ _jsx(Form.Item, {
                                label: __("Tax number"),
                                name: [
                                    "affiliate",
                                    "invoiceAddress",
                                    "taxNumber"
                                ],
                                extra: __("Shown on your payout receipts.") + (isCompanyIdentificationNumberRequired ? ` ${__("If your country uses one number for both, repeat the company identification number here.")}` : ""),
                                rules: [
                                    {
                                        required: true,
                                        message: __("Please enter your tax number!")
                                    }
                                ],
                                children: /*#__PURE__*/ _jsx(Input, {
                                    placeholder: isGermany ? __("e.g. 123/456/78912") : undefined,
                                    ...MATOMO_MASK
                                })
                            }),
                            isTradeRegisterExcerpt && /*#__PURE__*/ _jsx(Form.Item, {
                                noStyle: true,
                                shouldUpdate: updateOnAddressChangeFields,
                                children: ({ getFieldValue: getFieldValueInner })=>{
                                    const currentValues = AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS.map((k)=>getFieldValueInner([
                                            "affiliate",
                                            "invoiceAddress",
                                            k
                                        ]));
                                    const savedValues = AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS.map((k)=>session?.affiliate?.affiliate?.invoiceAddress?.[k]);
                                    const isTradeRegisterExcerptRequired = isEdit && session?.affiliate ? JSON.stringify(currentValues) !== JSON.stringify(savedValues) : true;
                                    return /*#__PURE__*/ _jsx(Form.Item, {
                                        label: buildTradeRegisterLabel(isGermany, isAustria),
                                        name: [
                                            "affiliate",
                                            "invoiceAddress",
                                            "tradeRegisterExcerpt"
                                        ],
                                        valuePropName: "fileList",
                                        getValueFromEvent: (e)=>Array.isArray(e) ? e : e?.fileList,
                                        required: isTradeRegisterExcerptRequired,
                                        rules: isTradeRegisterExcerptRequired ? [
                                            {
                                                validator: (_rule, value)=>value?.length ? Promise.resolve() : Promise.reject(new Error(__("Please select the document as PDF file!")))
                                            }
                                        ] : [],
                                        children: /*#__PURE__*/ _jsx(Upload, {
                                            accept: "application/pdf",
                                            maxCount: 1,
                                            beforeUpload: (file)=>{
                                                setTradeRegisterExcerptFile(file);
                                                // Keep the file client-side; we submit it ourselves via multipart.
                                                return false;
                                            },
                                            onRemove: ()=>setTradeRegisterExcerptFile(undefined),
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                icon: /*#__PURE__*/ _jsx(UploadOutlined, {}),
                                                children: __("Choose PDF file")
                                            })
                                        })
                                    });
                                }
                            })
                        ]
                    });
                }
            })
        ]
    });
});
export { AffiliateInvoiceAddressFields };
